import React, { useState, useEffect, useRef } from 'react';
import { ASCII } from '../profileAscii';

const BASE = process.env.PUBLIC_URL + '/profile';
const SRC = {
  formal: 'portrait',
  casual: 'casual',
};

// Portrait rendered the way this site renders everything: as terminal output.
// It first shows as ASCII art (pre-rendered in the site's monospace font),
// then "decodes" into the real photo — tinted with the active palette until
// you hover it. `--formal` / `--casual` swap the shot like a CLI flag.
export default function Portrait({ t }) {
  const [mode, setMode] = useState('formal');
  const [decoded, setDecoded] = useState(false);
  const [colour, setColour] = useState(false);   // tap-to-colour on touch devices
  const ref = useRef(null);
  const timer = useRef(0);

  // Decode the first time the section scrolls into view.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setDecoded(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      timer.current = setTimeout(() => setDecoded(true), 500);
      io.disconnect();
    }, { threshold: 0.35 });
    io.observe(el);
    return () => { io.disconnect(); clearTimeout(timer.current); };
  }, []);

  // Warm the other shot so the swap never shows a blank frame.
  useEffect(() => {
    if (!decoded) return;
    const other = mode === 'formal' ? SRC.casual : SRC.formal;
    const img = new Image();
    img.src = `${BASE}/${other}-720.webp`;
  }, [decoded, mode]);

  const switchMode = (next) => {
    if (next === mode) return;
    clearTimeout(timer.current);
    setDecoded(false);              // re-encode…
    setMode(next);
    timer.current = setTimeout(() => setDecoded(true), 650); // …then decode the new shot
  };

  const name = SRC[mode];
  const a = t.about;

  return (
    <figure className="portrait" ref={ref} data-decoded={decoded ? 'true' : 'false'}>
      <div
        className={`portrait__frame tgt${colour ? ' is-colour' : ''}`}
        onClick={() => decoded && setColour((c) => !c)}
      >
        <picture className="portrait__photo">
          <source
            type="image/webp"
            srcSet={`${BASE}/${name}-720.webp 720w, ${BASE}/${name}-1080.webp 1080w`}
            sizes="(max-width: 900px) 280px, 336px"
          />
          <img
            src={`${BASE}/${name}-720.jpg`}
            srcSet={`${BASE}/${name}-720.jpg 720w, ${BASE}/${name}-1080.jpg 1080w`}
            sizes="(max-width: 900px) 280px, 336px"
            width="720"
            height="900"
            alt={a.photoAlt}
            loading="lazy"
            decoding="async"
          />
        </picture>
        <pre className="portrait__ascii portrait__ascii--light" aria-hidden="true">{ASCII[mode + 'Light']}</pre>
        <pre className="portrait__ascii portrait__ascii--dark" aria-hidden="true">{ASCII[mode + 'Dark']}</pre>
      </div>
      <figcaption className="portrait__cap">
        <div className="portrait__cmd">
          <span className="term__prompt">$</span> {a.photoCmd}
        </div>
        <div className="portrait__modes" role="group" aria-label={a.photoModes}>
          <button type="button" className={mode === 'formal' ? 'is-on' : ''} aria-pressed={mode === 'formal'} onClick={() => switchMode('formal')}>--formal</button>
          <button type="button" className={mode === 'casual' ? 'is-on' : ''} aria-pressed={mode === 'casual'} onClick={() => switchMode('casual')}>--casual</button>
        </div>
        <div className="portrait__meta">
          {a.photoCaption}
          <span className="portrait__hint portrait__hint--hover"> · {a.photoHintHover}</span>
          <span className="portrait__hint portrait__hint--tap"> · {a.photoHintTap}</span>
        </div>
      </figcaption>
    </figure>
  );
}
